import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Users, Download, Loader2, Search, UserCircle, 
  CheckCircle, XCircle, Clock, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { safeFetchJson } from "@/utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge styles
const STATUS_STYLES = {
  invited: { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock },
  accepted: { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle },
  declined: { bg: "bg-red-100", text: "text-red-800", icon: XCircle },
  attended: { bg: "bg-blue-100", text: "text-blue-800", icon: CheckCircle }
};

export default function EventParticipantsModal({ 
  isOpen, 
  onClose, 
  event 
}) {
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen && event?.event_id) {
      fetchParticipants();
    }
  }, [isOpen, event?.event_id]);

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const parsed = await safeFetchJson(`${API_URL}/api/events/${event.event_id}/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (parsed.ok) {
        setParticipants(parsed.data || []);
      } else {
        throw new Error(parsed.data?.detail || "Failed to load participants");
      }
    } catch (error) {
      console.error("Error fetching participants:", error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/events/${event.event_id}/participants/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error("Failed to export participants");
      }

      // Get filename from header or generate one
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "participants.csv";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("CSV exported successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  // Filter participants by search
  const filteredParticipants = participants.filter(p => {
    if (!searchQuery) return true;
    const name = (p.user_info?.display_name || p.user_info?.name || "").toLowerCase();
    const email = (p.user_info?.email || "").toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || email.includes(query);
  });

  // Count by status
  const statusCounts = participants.reduce((acc, p) => {
    const status = p.status || "invited";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="participants-modal">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-violet-600" />
            Participants - {event.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Stats Summary */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Badge variant="outline" className="px-3 py-1">
              Total: {participants.length}
            </Badge>
            {statusCounts.invited > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800 px-3 py-1">
                <Clock className="w-3 h-3 mr-1" />
                Pending: {statusCounts.invited}
              </Badge>
            )}
            {statusCounts.accepted > 0 && (
              <Badge className="bg-green-100 text-green-800 px-3 py-1">
                <CheckCircle className="w-3 h-3 mr-1" />
                Accepted: {statusCounts.accepted}
              </Badge>
            )}
            {statusCounts.declined > 0 && (
              <Badge className="bg-red-100 text-red-800 px-3 py-1">
                <XCircle className="w-3 h-3 mr-1" />
                Declined: {statusCounts.declined}
              </Badge>
            )}
          </div>

          {/* Search & Export */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting || participants.length === 0}
              data-testid="export-csv-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>

          {/* Participants List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
              <span className="ml-2 text-gray-500">Loading participants...</span>
            </div>
          ) : filteredParticipants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{searchQuery ? "No matching participants" : "No participants yet"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredParticipants.map(p => {
                const statusStyle = STATUS_STYLES[p.status] || STATUS_STYLES.invited;
                const StatusIcon = statusStyle.icon;
                
                return (
                  <div
                    key={p.participant_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      {p.user_info?.profile_photo ? (
                        <img
                          src={p.user_info.profile_photo}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                          <UserCircle className="w-5 h-5 text-violet-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {p.user_info?.display_name || p.user_info?.name || "Unknown"}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {p.user_info?.profile_type || "Talent"}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {p.status?.charAt(0).toUpperCase() + p.status?.slice(1) || "Invited"}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t pt-4 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
